import React from 'react'
import styles from 'components/pages/support/help/helpStyles'
import { HelpComponentProps } from 'types/Help'
import { useFormatMessage } from 'lib/localization'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'

const BASE_I18N_KEY = 'ess.help_reports_all_domains'

function ReportsAllDomainsI18n({ classes, productName }: { classes: ReturnType<typeof styles>; productName?: string }) {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatValues = { productName }

  return (
    <div className={classes.content} data-testid="content">
      <p>{formatMessage('intro_paragraph_1', formatValues)}</p>
      <p>{formatMessage('intro_paragraph_2')}</p>
      {formatMessage('report_columns_intro')}
      <ul>
        <li>
          <b>{formatMessage('columns.date.title')}</b>
          <br />
          {formatMessage('columns.date.description')}
        </li>
        <li>
          <b>{formatMessage('columns.deferred.title')}</b>
          <br />
          {formatMessage('columns.deferred.description')}
        </li>
        <li>
          <b>{formatMessage('columns.blocked_rate_control.title')}</b>
          <br />
          {formatMessage('columns.blocked_rate_control.description')}
        </li>
        <li>
          <b>{formatMessage('columns.blocked_policy.title')}</b>
          <br />
          {formatMessage('columns.blocked_policy.description')}
        </li>
        <li>
          <b>{formatMessage('columns.blocked_spam.title')}</b>
          <br />
          {formatMessage('columns.blocked_spam.description')}
        </li>
        <li>
          <b>{formatMessage('columns.blocked_virus.title')}</b>
          <br />
          {formatMessage('columns.blocked_virus.description')}
        </li>
        <li>
          <b>{formatMessage('columns.blocked_atp.title')}</b>
          <br />
          {formatMessage('columns.blocked_atp.description')}
        </li>
        <li>
          <b>{formatMessage('columns.blocked_other.title')}</b>
          <br />
          {formatMessage('columns.blocked_other.description')}
        </li>
        <li>
          <b>{formatMessage('columns.blocked_total.title')}</b>
          <br />
          {formatMessage('columns.blocked_total.description')}
        </li>
        <li>
          <b>{formatMessage('columns.quarantined.title')}</b>
          <br />
          {formatMessage('columns.quarantined.description')}
        </li>
        <li>
          <b>{formatMessage('columns.allowed.title')}</b>
          <br />
          {formatMessage('columns.allowed.description')}
        </li>
        <li>
          <b>{formatMessage('columns.encrypted.title')}</b>
          <br />
          {formatMessage('columns.encrypted.description')}
        </li>
        <li>
          <b>{formatMessage('columns.total_received.title')}</b>
          <br />
          {formatMessage('columns.total_received.description', formatValues)}
        </li>
      </ul>
      <p>{formatMessage('other_reports_intro')}</p>
      <ul>
        <li>
          <b>{formatMessage('report_types.top_email_senders.title')}</b>
          <br />
          {formatMessage('report_types.top_email_senders.description')}
          <ul>
            <li>{formatMessage('report_types.top_email_senders.columns')}</li>
          </ul>
        </li>

        <li>
          <b>{formatMessage('report_types.top_email_recipients.title')}</b>
          <br />
          {formatMessage('report_types.top_email_recipients.description')}
          <ul>
            <li>{formatMessage('report_types.top_email_recipients.columns')}</li>
          </ul>
        </li>

        <li>
          <b>{formatMessage('report_types.top_spam_senders.title')}</b>
          <br />
          {formatMessage('report_types.top_spam_senders.description')}
          <ul>
            <li>{formatMessage('report_types.top_spam_senders.columns')}</li>
          </ul>
        </li>

        <li>
          <b>{formatMessage('report_types.top_spam_recipients.title')}</b>
          <br />
          {formatMessage('report_types.top_spam_recipients.description')}
          <ul>
            <li>{formatMessage('report_types.top_spam_recipients.columns')}</li>
          </ul>
        </li>

        <li>
          <b>{formatMessage('report_types.top_virus_senders.title')}</b>
          <br />
          {formatMessage('report_types.top_virus_senders.description')}
          <ul>
            <li>{formatMessage('report_types.top_virus_senders.columns')}</li>
          </ul>
        </li>

        <li>
          <b>{formatMessage('report_types.top_virus_recipients.title')}</b>
          <br />
          {formatMessage('report_types.top_virus_recipients.description')}
          <ul>
            <li>{formatMessage('report_types.top_virus_recipients.columns')}</li>
          </ul>
        </li>

        <li>
          <b>{formatMessage('report_types.top_blocked_senders.title')}</b>
          <br />
          {formatMessage('report_types.top_blocked_senders.description')}
          <ul>
            <li>{formatMessage('report_types.top_blocked_senders.columns')}</li>
          </ul>
        </li>

        <li>
          <b>{formatMessage('report_types.top_blocked_recipients.title')}</b>
          <br />
          {formatMessage('report_types.top_blocked_recipients.description')}
          <ul>
            <li>{formatMessage('report_types.top_blocked_recipients.columns')}</li>
          </ul>
        </li>
      </ul>
    </div>
  )
}

function ReportsAllDomains(props: HelpComponentProps) {
  const classes = styles()
  let isSmartlingEnabled = false
  try {
    isSmartlingEnabled = isMyFeatureOn(FEATURES.EGD_React_Smartling_Translations)
  } catch {
    // Default to OFF when called outside Redux context (e.g., renderToString for search)
  }

  // Flag ON: Use translated content
  if (isSmartlingEnabled) {
    return <ReportsAllDomainsI18n classes={classes} productName={props.productName} />
  }

  // Flag OFF: Original hardcoded English content
  return (
    <div className={classes.content} data-testid="content">
      <p>
        Select the report type from the <b>Report Type</b> drop-down menu. Each of the <b>Inbound Traffic</b> and{' '}
        <b>Outbound Traffic</b> reports summarize all messages processed through {props.productName} for this domain.
      </p>
      <p>
        Generate reports for up to a thirty day period. You can set the <b>Start Date</b> for up to 30 days in the past
        from the current date based on the default time zone of the account. Click in the <b>Start Date</b> field, and
        select the start date from the calendar. Click <b>Show Report</b> to display the report summary, or click{' '}
        <b>Export</b> to download the report as a .<b>csv</b> file to your local system.
      </p>
      Report columns include:
      <ul>
        <li>
          <b>Date</b>
          <br />
          Date messages were received. The date format is YYYY-MM-DD (Example: 2011-11-20).
        </li>
        <li>
          <b>Deferred</b>
          <br />
          Total number of deferred messages.
        </li>
        <li>
          <b>Blocked:Rate Control</b>
          <br />
          Number of messages blocked due to Rate Control.
        </li>
        <li>
          <b>Blocked:Policy</b>
          <br />
          Number of messages blocked due to policy.
        </li>
        <li>
          <b>Blocked:Spam</b>
          <br />
          Number of messages blocked due to spam.
        </li>
        <li>
          <b>Blocked:Virus</b>
          <br />
          Number of messages blocked due to viruses.
        </li>
        <li>
          <b>Blocked:ATP</b>
          <br />
          Number of messages blocked due to Advanced Threat Protection (ATP).
        </li>
        <li>
          <b>Blocked:Other</b>
          <br />
          Number of messages blocked due to Possible Mail Loop, Malformed email, or Invalid Recipient.
        </li>
        <li>
          <b>Blocked:Total</b>
          <br />
          Total number of blocked messages.
        </li>
        <li>
          <b>Quarantined</b>
          <br />
          Number of messages quarantined.
        </li>
        <li>
          <b>Allowed</b>
          <br />
          Number of messages allowed.
        </li>
        <li>
          <b>Encrypted</b>
          <br />
          Number of messages encrypted (<i>outbound</i> traffic only).
        </li>
        <li>
          <b>Total Received</b>
          <br />
          Total number of messages processed from the Internet through {props.productName} to this domain.
        </li>
      </ul>
      <p>
        Other available reports (with report columns) are listed below. Use the <b>Top</b> field to specify the number
        of results, from the highest number down, to show in the report.
      </p>
      <ul>
        <li>
          <b>Top Email Senders</b>
          <br />
          Senders of the most messages.
          <ul>
            <li>
              Deferred, Blocked:Rate Control, Blocked:Policy, Blocked:Spam, Blocked:Virus, Blocked:ATP, Blocked:Other,
              Blocked:Total, Quarantined, Allowed, Encrypted, Total Received
            </li>
          </ul>
        </li>

        <li>
          <b>Top Email Recipients</b>
          <br />
          Recipients of the most messages.
          <ul>
            <li>
              Deferred, Blocked:Rate Control, Blocked:Policy, Blocked:Spam, Blocked:Virus, Blocked:ATP, Blocked:Other,
              Blocked:Total, Quarantined, Allowed, Encrypted, Total Received
            </li>
          </ul>
        </li>

        <li>
          <b>Top Spam Senders</b>
          <br />
          Senders of the most messages identified as Spam.
          <ul>
            <li>Blocked:Spam, Blocked:Other, Total Received</li>
          </ul>
        </li>

        <li>
          <b>Top Spam Recipients</b>
          <br />
          Recipients of the most messages identified as Spam.
          <ul>
            <li>Blocked:Spam, Blocked:Other, Total Received</li>
          </ul>
        </li>

        <li>
          <b>Top Virus Senders</b>
          <br />
          Senders of the most messages containing viruses.
          <ul>
            <li>Blocked:Virus, Blocked:ATP, Total Received</li>
          </ul>
        </li>

        <li>
          <b>Top Virus Recipients</b>
          <br />
          Recipients of the most messages containing viruses.
          <ul>
            <li>Blocked:Virus, Blocked:ATP, Total Received</li>
          </ul>
        </li>

        <li>
          <b>Top Blocked Senders</b>
          <br />
          Senders of the greatest number of blocked messages.
          <ul>
            <li>Blocked:Policy, Blocked:Spam, Blocked:Virus, Blocked:ATP, Blocked:Other, Total Received</li>
          </ul>
        </li>

        <li>
          <b>Top Blocked Recipients</b>
          <br />
          Recipients of the greatest number of blocked messages.
          <ul>
            <li>Blocked:Policy, Blocked:Spam, Blocked:Virus, Blocked:ATP, Blocked:Other, Total Received</li>
          </ul>
        </li>
      </ul>
    </div>
  )
}

export default ReportsAllDomains
